import React, { useState, useEffect } from 'react'
import {
  Form,
  Input,
  Button,
  Card,
  message,
  Spin,
  Typography,
  Checkbox
} from 'antd'
import {
  UserOutlined,
  LockOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
  SafetyCertificateOutlined,
  BankOutlined,
  PhoneOutlined
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../Core/Auth/AuthContext'
import { client } from '../../Core/client'

const { Title, Text, Paragraph } = Typography

const Login = () => {
  const [loading, setLoading] = useState(false)
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const { login, isAuthenticated } = useAuth()

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true })
    }
  }, [isAuthenticated, navigate])

  const onFinish = async values => {
    setLoading(true)
    try {
      console.log('Dealer login attempt:', values)

      const loginData = {
        contact_no: values.contact_no,
        password: values.password
      }

      // Call dealer login API
      const response = await client.post('/auth/login', loginData)

      if (response.data.success) {
        const { dealer, token } = response.data

        // Store dealer information and token
        const dealerData = {
          id: dealer.id,
          dealerName: dealer.dealerName,
          email: dealer.email,
          mobile: dealer.mobile,
          district: dealer.district,
          state: dealer.state,
          addressLineOne: dealer.addressLineOne,
          pincode: dealer.pincode,
          loginTime: new Date().toISOString(),
          role: 'dealer'
        }

        // Store JWT token
        if (values.remember) {
          localStorage.setItem('dealer_token', token)
          localStorage.setItem('dealer_info', JSON.stringify(dealerData))
        } else {
          sessionStorage.setItem('dealer_token', token)
          sessionStorage.setItem('dealer_info', JSON.stringify(dealerData))
        }

        // Update auth context
        login(dealerData, token, values.remember)

        message.success(`Welcome back, ${dealer.dealerName}!`)

        // Navigate to warranty registration or dashboard
        navigate('/alloy-registration')
      } else {
        throw new Error(response.data.message || 'Login failed')
      }
    } catch (error) {
      console.error('Login error:', error)

      let errorMessage = 'Login failed. Please try again.'

      if (error.response) {
        switch (error.response.status) {
          case 400:
            errorMessage = 'Invalid contact number or password'
            break
          case 401:
            errorMessage = 'Incorrect contact number or password'
            break
          case 404:
            errorMessage = 'Dealer account not found. Please contact support.'
            break
          case 500:
            errorMessage = 'Server error. Please try again later.'
            break
          default:
            errorMessage = error.response.data?.message || errorMessage
        }
      }

      message.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const onFinishFailed = errorInfo => {
    console.log('Login form failed:', errorInfo)
    message.error('Please fill in all required fields correctly')
  }

  const handleForgotPassword = () => {
    message.info('Please contact support for password reset assistance')
  }

  return (
    <div className='min-h-screen bg-gradient-to-br from-red-50 to-gray-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8'>
      <div className='max-w-md w-full'>
        {/* Header */}
        <div className='text-center mb-8'>
          <div className='flex justify-center items-center'>
            <img
              src='/images/Plati.png'
              alt='Plati Logo'
              className='w-32 h-16 mb-4'
            />
          </div>
          <Title level={2} className='text-gray-900 mb-2'>
            Dealer Login
          </Title>
          <Paragraph className='text-gray-600'>
            Sign in to your PLATI WARRANTY dealer account
          </Paragraph>
        </div>

        {/* Login Card */}
        <Card className='shadow-xl border-0 rounded-xl overflow-hidden'>
          <div className='p-6'>
            <Form
              form={form}
              name='dealerLogin'
              layout='vertical'
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              autoComplete='off'
              size='large'
            >
              <Form.Item
                label={
                  <span className='text-gray-700 font-medium'>
                    <PhoneOutlined className='mr-2' />
                    Contact Number
                  </span>
                }
                name='contact_no'
                rules={[
                  {
                    required: true,
                    message: 'Please input your contact number!'
                  },
                  {
                    pattern: /^[6-9]\d{9}$/,
                    message: 'Please enter a valid 10-digit mobile number!'
                  }
                ]}
              >
                <Input
                  placeholder='Enter your registered mobile number'
                  className='rounded-lg focus:border-red-500 focus:ring-red-500'
                  maxLength={10}
                />
              </Form.Item>

              <Form.Item
                label={
                  <span className='text-gray-700 font-medium'>
                    <LockOutlined className='mr-2' />
                    Password
                  </span>
                }
                name='password'
                rules={[
                  {
                    required: true,
                    message: 'Please input your password!'
                  },
                  {
                    min: 6,
                    message: 'Password must be at least 6 characters!'
                  }
                ]}
              >
                <Input.Password
                  placeholder='Enter your password'
                  iconRender={visible =>
                    visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                  }
                  className='rounded-lg border-gray-300 focus:border-red-500 focus:ring-red-500'
                />
              </Form.Item>

              <div className='flex items-center justify-between mb-6'>
                <Form.Item
                  name='remember'
                  valuePropName='checked'
                  className='mb-0'
                >
                  <Checkbox className='text-sm text-gray-600'>
                    Remember me
                  </Checkbox>
                </Form.Item>
                <Button
                  type='link'
                  className='text-sm text-red-600 hover:text-red-500 font-medium p-0'
                  onClick={handleForgotPassword}
                >
                  Forgot password?
                </Button>
              </div>

              <Form.Item className='mb-0'>
                <Button
                  type='primary'
                  htmlType='submit'
                  loading={loading}
                  className='w-full h-12 bg-red-600 hover:bg-red-700 border-red-600 rounded-lg text-base font-medium'
                  disabled={loading}
                  icon={loading ? null : <SafetyCertificateOutlined />}
                >
                  {loading ? (
                    <>
                      <Spin size='small' className='mr-2' />
                      Signing In...
                    </>
                  ) : (
                    'Sign In as Dealer'
                  )}
                </Button>
              </Form.Item>
            </Form>
          </div>
        </Card>

        {/* Features Info */}
        {/* <div className='mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200'>
          <Title level={5} className='text-blue-800 mb-2'>
            <SafetyCertificateOutlined className='mr-2' />
            Dealer Portal Features:
          </Title>
          <ul className='text-sm text-blue-700 space-y-1'>
            <li>• Register warranty for alloy wheels and tyres</li>
            <li>• View and manage your warranty registrations</li>
            <li>• Track verification status and statistics</li>
            <li>• Access customer support and documentation</li>
          </ul>
        </div> */}

        {/* Support Info
        <div className='mt-4 text-center'>
          <Text className='text-gray-600 text-sm'>
            Need help? Contact support at{' '}
            <a
              href='tel:+911234567890'
              className='text-red-600 hover:text-red-500'
            >
              +91-123-456-7890
            </a>
          </Text>
        </div> */}
      </div>
    </div>
  )
}

export default Login
